/* @flow */
import invariant from 'invariant';
import jwt from 'lib/jsonwebtoken';
import React from 'react';
import { Container } from 'flux/utils';

import AuthActions from 'auth/AuthActions';
import BoxBody from 'components/BoxBody';
import BoxFooter from 'components/BoxFooter';
import Button from 'components/Button';
import Copy from 'components/Copy';
import CurrentUser from 'auth/CurrentUser';
import DeviceActions from 'actions/DeviceActions';
import DeviceStore from 'stores/DeviceStore';
import DialogContent from 'components/DialogContent';
import ErrorActions from 'actions/ErrorActions';
import Link from 'components/Link';
import UserActions from 'user/UserActions';
import EventTracking from 'lib/EventTracking';
import ToastActions from 'actions/ToastActions';

import type Device from 'entities/Device';
import type { RouterState } from 'lib/ReactRouterTypes';

import './styles/EmailUnsubscribeView.scss';

type Props = {};

type State = {
  device?: Device,
  enabled?: Boolean,
};

export class EmailUnsubscribeView extends React.Component<Props, State> {
  static onEnter(nextState: RouterState, replaceWith: Function, callback: Function) {
    const payload = jwt.decode(nextState.params.token);

    const loginAndGetDevice = () => {
      AuthActions.jwtTokenSignIn(nextState.params.token);
      UserActions.loadCurrentUser(nextState.params.token)
        .then(
          () => {
            return DeviceActions.loadDevices();
          },
          () => {
            ErrorActions.expiredToken('EMAIL_UNSUBSCRIBE');
            replaceWith('/error');
            callback();
          }
        )
        .then(
          () => {
            const device = DeviceStore.getDevice(payload.device_id);
            if (!device) {
              replaceWith('/error');
            }
            callback();
          },
          () => {
            replaceWith('/error');
            callback();
          }
        );
    };

    // If there is a signed in user who does not own the device,
    // log out and refresh the page to log in as the user we want
    AuthActions.checkLoggedIn().then(
      user => {
        // $FlowFixMe
        if (user.uuid === payload.uuid) {
          loginAndGetDevice();
        } else {
          AuthActions.signOut(nextState.location.pathname);
        }
      },
      () => loginAndGetDevice()
    );
  }

  static getStores() {
    return [CurrentUser, DeviceStore];
  }

  static calculateState(): State {
    const payload = CurrentUser.getTokenPayload();
    const device = payload && payload.device_id && DeviceStore.getDevice(payload.device_id);

    if (!device) {
      return {};
    }

    return { device, enabled: device.isEnabled() };
  }

  constructor(props: Props) {
    super(props);

    this.state = EmailUnsubscribeView.calculateState();
  }

  componentWillMount() {
    if (!DeviceStore.isLoaded()) {
      DeviceActions.loadDevices();
    }

    EventTracking.track('email_unsubscribe.loaded');
  }

  handleDisableButtonClick = () => {
    EventTracking.track('email_unsubscribe.disabled_clicked');
    invariant(this.state.device, 'EmailUnsubscribeView handleDisableButtonClick missing device');
    DeviceActions.disable(this.state.device)
      .then(() => {
        EventTracking.track('email_unsubscribe.disabled');
      })
      .catch(() =>
        ToastActions.showErrorToast(
          'An error occurred. Please try again later or contact support to unsubscribe.'
        )
      );
  };

  goToLoginThenNotificationSettings = () => {
    // Using Link's to prop goes through react-router's Link, which causes
    //   graphql queries to be made that return 401. It's simpler to just
    //   manually redirect the user, avoiding any token issues
    window.location = '/log_in?locationOnAuth=/settings/notifications';
  };

  renderConfirmation() {
    invariant(this.state.device, 'EmailUnsubscribeView renderConfirmation missing device');
    return (
      <DialogContent
        size="small"
        title={
          <span>
            Are you sure you want to stop receiving messages from <b>all</b> your classes via email?
          </span>
        }
        glyph="caution"
        glyphColor="lava">
        <BoxBody className="EmailUnsubscribeView-box">
          <Copy className="EmailUnsubscribeView-box-copy">
            <p>
              Click on the button below to stop receiving messages from all of your classes via
              email at {this.state.device.getLabel()}. If you’d like to edit your contact
              information, change your notification preferences, or download the Remind app,{' '}
              <Link onClick={this.goToLoginThenNotificationSettings}>visit your account</Link>{' '}
              instead.
            </p>
          </Copy>
          <Button onClick={this.handleDisableButtonClick}>{'Unsubscribe'}</Button>
        </BoxBody>
        <BoxFooter>
          Click <Link onClick={this.goToLoginThenNotificationSettings}>here</Link> to edit message
          preferences
        </BoxFooter>
      </DialogContent>
    );
  }

  renderDisabledMessage() {
    invariant(this.state.device, 'EmailUnsubscribeView renderDisabledMessage missing device');
    const title = `Your email (${this.state.device.getLabel()}) is disabled from receiving Remind messages.`;
    const subtext = 'You can now close this browser.';
    return <DialogContent size="small" title={title} subtext={subtext} />;
  }

  render() {
    if (!this.state.device) {
      return null;
    } else if (this.state.enabled) {
      return this.renderConfirmation();
    } else {
      return this.renderDisabledMessage();
    }
  }
}
export default Container.create(EmailUnsubscribeView);
